<template>
  <div class="card">
    <div class="card-header">
      <h6>{{ $t(type === 'open' ? 'opentrades' : 'historytrades') }}</h6>
    </div>
    <div class="card-body row">
      <!-- Search -->
      <div class="col-10">
        <fg-input v-model="filters.term" :label="$t('account')"></fg-input>
      </div>
      <!-- Export Button -->
      <div class="col-2 d-flex align-items-end float-right">
        <div class="btn-group mr-1" role="group">
          <p-button @click="refreshList">
            <i class="fa fa-refresh"></i>
          </p-button>
          <p-button type="warning" :title="$t('exportcsv')" @click="refreshList(true)">
            <i class="fa fa-download"></i>
          </p-button>
        </div>
      </div>
    </div>
    <div class="card-body">
      <data-tables-server
        ref="datatable"
        v-loading="tableLoading"
        :total="total"
        :data="data"
        :table-props="configTable"
        :pagination-props="configPaginator"
        :filters="listFilters"
        @query-change="loadData"
      >
        <div slot="empty">{{ $t('norecordsfounded') }}</div>
        <span slot="pagination" class="el-pagination__total ml-2">{{$t('total_records_found',[total])}}</span>
        <el-table-column
          prop="orderNum"
          :label="getHeader('orderNum')"
          align="center"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderType"
          :label="getHeader('orderType')"
          align="center"
          width="90"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderSymbol"
          :label="getHeader('orderSymbol')"
          align="center"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderLots"
          :label="getHeader('orderLots')"
          align="center"
          width="75"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderOpenTime"
          :label="getHeader('orderOpenTime')"
          :formatter="formatDateColumn"
          align="center"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderCloseTime"
          :label="getHeader('orderCloseTime')"
          :formatter="formatDateColumn"
          align="center"
          sortable
          show-overflow-tooltip
          v-if="!isOpen"
        ></el-table-column>
        <el-table-column
          prop="orderOpenPrice"
          :label="getHeader('orderOpenPrice')"
          align="center"
          width="90"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderClosePrice"
          :label="getHeader('orderClosePrice')"
          align="center"
          width="90"
          sortable
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderSwap"
          :label="getHeader('orderSwap')"
          align="center"
          width="90"
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="orderCommission"
          :label="getHeader('orderCommission')"
          align="center"
          width="90"
          show-overflow-tooltip
          v-if="!isOpen"
        ></el-table-column>
        <el-table-column
          prop="orderProfit"
          :label="getHeader('orderProfit')"
          align="center"
          width="90"
          show-overflow-tooltip
        ></el-table-column>
      </data-tables-server>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { Pagination } from 'element-ui';
import { DataTablesServer } from 'vue-data-tables';
import { formatDate } from 'src/util/DateUtils';
import { ListPayload, exportCSVFile } from "src/models/commons";
import Constants from 'src/assets/constants.js';

const CONST = Constants.methods;
const CPSTK = CONST.cpIntegrationConf()['cpstk'];

Vue.use(DataTablesServer);
Vue.use(Pagination);

export default {
  name: 'AccountTradesTable',
  components: {},
  props: {
    account: {
      type: String,
      required: true
    },
    type: {
      type: String,
      required: true,
      validator: (val) => ['open', 'history'].includes(val)
    }
  },
  computed: {
    isOpen() {
      return this.type === 'open';
    },
    configTable() {
      return {
        height: 640,
        highlightCurrentRow: true,
        stripe: true,
        sortBy: this.isOpen ? 'orderOpenTime' : 'orderCloseTime',
        descending: false,
        itemsPerPage: 50,
      };
    },
    configPaginator() {
      return {
        layout: "sizes, prev, pager, next, jumper, slot, ->"
      };
    },
  },
  data () {
    return {
      data: [],
      filters: {term: ''},
      fixedFilters: {'account': this.account},
      listFilters: [],
      total: 0,
      sortBy: { prop: 'open_time', order: 'desc' },
      tableLoading: false,
      tableHeaders: {
        orderNum: this.$t('ordernum'),
        orderType: this.$t('type'),
        orderSymbol: this.$t('ordersymbol'),
        orderLots: this.$t('orderlots'),
        orderOpenTime: this.$t('orderopentime'),
        orderCloseTime: this.$t('orderclosetime'),
        orderOpenPrice: this.$t('orderopenprice'),
        orderClosePrice: this.$t('ordercloseprice'),
        orderSwap: this.$t('orderswap'),
        orderCommission: this.$t('ordercommission'),
        orderProfit: this.$t('total_pl'),
      },
    }
  },
  methods: {
    formatDateColumn(row, column, cellValue) {
      return formatDate(cellValue, 'YYYY-MM-DD HH:mm:ss');
    },
    getHeader(prop) {
      return this.tableHeaders[prop];
    },
    clearFilters() {
      this.beforeClearFilters();
    },
    refreshList(exporting) {
      this.tableLoading = true;
      this.data = [];
      this.total = 0;
      this.listFilters = this.$noBlankProps(Object.assign({}, this.filters, this.fixedFilters));
      this.$nextTick(() => {
        this.exporting = typeof exporting === 'boolean' ? exporting : false;
        this.$refs.datatable.queryChange('refresh');
      });
    },
    loadData(payload) {
      if (payload.page === null) {
        return;
      }
      this.tableLoading = true;
      this.$search({
        apiUrl: CONST.chartsapiurl(),
        url: `/v1/strategy/search/${this.type}`,
        payload: this.$serializeToQueryString(new ListPayload({...payload, paginate: !this.exporting})),
        headers: {Authorization: `Bearer ${CPSTK}`},
      })
      .then(response => {
        if(this.exporting) {
          this.exportCSV(response)
        } else {
          this.fillList(response);
        }
      },)
      .catch((err) => {
        console.error(err);
      })
    },
    fillList(response) {
      if (!response.success && !response.data) {
        return;
      }
      this.data = response.data.results || [];
      this.total = response.data.count || 0;
      this.tableLoading = false;
    },
    handleExportData() {
      if (this.exportDataWithCheckbox && this.sendRows.length > 0) {
        return this.sendRows.map(obj => Object.values(obj).join(this.exportSeparator)).join('\r\n');
      } else {
        return this.exportData.map(obj => Object.values(obj).join(this.exportSeparator)).join('\r\n');
      }
    },
    async exportCSV(response) {
      this.exporting = false;
      if (!response.success && !response.data) {
        return;
      }
      await this.beforeExport(response.data.results || []);
      const headers = Object.values(this.tableHeaders).join(this.exportSeparator);
      const data = this.handleExportData();
      exportCSVFile(`${headers}\r\n${data}`);
      this.refreshList();
      this.sendRows = [];
    },
  },
  mounted () {
    if (this.fixedFilters && Object.keys(this.fixedFilters).length) {
      this.refreshList();
    }
  }
}
</script>
